import { Editor } from '@tiptap/core';
import { debounce } from 'lodash';

// Utils
import { isFormula } from '../../../bridge/utils/dataUtils/formula/tableFormulaPredicateUtils';
import {
    getAllCellsBetween,
    getMaxCellSelection,
    getMinCellSelection,
} from '../../../bridge/utils/selectionUtils/tableSelectionGeometryUtils';

// Types
import { TiptapContent } from '../../../../../../common/tiptap/tiptapTypes';
import getTiptapNodeTextContent from '../../../../../../common/tiptap/utils/jsonContentUtils/getTiptapNodeTextContent';
import TiptapCellEditor from '../../modules/TiptapCellEditor';
import { MilanoteHotPlugin } from '../MilanoteHotPlugin';
import { CellReference, getCellReferences } from './cellReferences';

export const FORMULA_REFERENCE_HIGHLIGHT_PLUGIN_NAME = 'FormulaReferenceHighlight';
const FORMULA_REFERENCE_HIGHLIGHTED_CLASS_NAME = 'formula-reference-highlighted';

class FormulaReferenceHighlightHotPlugin extends MilanoteHotPlugin {
    private prevText: string | null = null; // only used to determine if re-highlight of cell references is needed

    private customBordersAdded = false;

    private cellReferences: CellReference[] = [];

    /**
     * This key will be used as a prop key when enabling this plugin in the HotTable component
     */
    static get PLUGIN_KEY(): string {
        return FORMULA_REFERENCE_HIGHLIGHT_PLUGIN_NAME;
    }

    /**
     * The `enablePlugin` method is triggered on the `beforeInit` hook.
     * It should contain the plugin's initial setup and hook connections.
     * This method is run only if the `isEnabled` method returns `true`.
     */
    enablePlugin(): void {
        this.addHook('beforeChange', () => this.onBeforeChange());

        this.addMilanoteHook('onMilanoteCellEditorChange', (...args) =>
            this.debouncedOnMilanoteCellEditorChange(...args),
        );

        // The `super` method sets the `this.enabled` property to `true`.
        // It is a necessary step to update the plugin's settings properly.
        super.enablePlugin();
    }

    /**
     * Prevents queued highlight work from running after Handsontable has torn down the plugin.
     */
    disablePlugin(): void {
        this.debouncedOnMilanoteCellEditorChange.cancel();
        super.disablePlugin();
    }

    /******************
     * PLUGIN HOOKS
     ******************/

    onMilanoteCellEditorChange(content: TiptapContent): void {
        if (!this.milanoteHot.getActiveEditor()?.isOpened()) return;
        if (!this.milanoteHot.isListening()) return;

        const currentText = getTiptapNodeTextContent(content);

        if (!isFormula(currentText)) {
            this.removeCellSelectionReferences();
            return;
        }

        if (this.prevText === currentText) return;

        this.highlightCellSelectionReferences(currentText);
        this.prevText = currentText;
    }

    // Calls to the highlighter need to be debounced a few ms; it avoids an issue where
    // highlighting fails when starting to edit a cell that contains existing references.
    debouncedOnMilanoteCellEditorChange = debounce(this.onMilanoteCellEditorChange, 3);

    onBeforeChange(): void {
        this.removeCellSelectionReferences();
    }

    /******************
     * CLEARING REFERENCES
     ******************/

    clearReferencedCellHighlights(): void {
        this.cellReferences.forEach(({ cellSelection }) => {
            getAllCellsBetween([cellSelection]).forEach(({ row, col }) => {
                const cellElement = this.milanoteHot.getCell(row, col);
                if (!cellElement) return;

                cellElement.classList.remove(FORMULA_REFERENCE_HIGHLIGHTED_CLASS_NAME);
            });
        });
    }

    removeCellSelectionReferences(): void {
        this.prevText = null;
        this.clearReferencedCellHighlights();
        this.cellReferences = [];
        this.clearCellHighlightTextColors();

        if (this.customBordersAdded) {
            this.milanoteHot.updateSettings({ customBorders: [] });
            this.customBordersAdded = false;
        }
    }

    /******************
     * ADDING HIGHLIGHTS
     ******************/

    addCellHighlightBorders(references: CellReference[]): void {
        this.customBordersAdded = true;

        this.milanoteHot.updateSettings({
            customBorders: references.map(({ cellSelection, borderColorHex }) => {
                const border = { width: 2, color: `#${borderColorHex}` };
                return {
                    range: {
                        from: getMinCellSelection(cellSelection),
                        to: getMaxCellSelection(cellSelection),
                    },
                    left: border,
                    right: border,
                    top: border,
                    bottom: border,
                };
            }),
        });
    }

    addCellHighlightBackgrounds(references: CellReference[]): void {
        references.forEach(({ cellSelection, backgroundColor }) => {
            const referencedCellCoords = getAllCellsBetween([cellSelection]);
            referencedCellCoords.forEach(({ row, col }) => {
                const cellElement = this.milanoteHot.getCell(row, col);
                if (!cellElement) return;

                cellElement.classList.add(FORMULA_REFERENCE_HIGHLIGHTED_CLASS_NAME);
                cellElement.style.setProperty('--ws-table-cell-reference-overlay-color', backgroundColor);
            });
        });
    }

    getTiptapEditor(): Editor | null {
        // Get ref to tiptap editor (this is a messy call but let's leave it until post Draft removal)
        const editor: Editor = (this.milanoteHot.getMNEditor() as TiptapCellEditor)
            ?.getEditorInstance()
            ?.getEditor() as Editor;

        return editor || null;
    }

    addCellHighlightTextColors(references: CellReference[]): void {
        const editor = this.getTiptapEditor();
        if (!editor) return;

        editor.commands.applyFormulaCellHighlights(references);
    }

    clearCellHighlightTextColors(): void {
        const editor = this.getTiptapEditor();
        if (!editor) return;

        editor.commands.clearFormulaCellHighlights();
    }

    /**
     * With the given cellReferences,
     *   1. Add borders and background color to the cells being referenced
     *   2. Inside the editor, apply text color to the cell references
     */
    highlightCellSelectionReferences(currentText: string): void {
        this.clearReferencedCellHighlights();

        const references = getCellReferences(currentText);

        this.cellReferences = references;

        if (references.length === 0) {
            this.clearCellHighlightTextColors();

            if (this.customBordersAdded) {
                this.milanoteHot.updateSettings({ customBorders: [] });
                this.customBordersAdded = false;
            }

            return;
        }

        this.addCellHighlightBorders(references);
        this.addCellHighlightBackgrounds(references);
        this.addCellHighlightTextColors(references);
    }
}

export default FormulaReferenceHighlightHotPlugin;
